import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useFormLogic } from "../../../hooks/useFormLogic";
import ReusableForm from "../../../components/ReusableForm/ReusableForm";
import { PropagateLoader } from "react-spinners";
import PopUp from "../../../components/PopUp/PopUp";
import useGeneralLogic from "./useGeneralLogic";
import axios from "axios";
import { config } from "../../../utils/config";
import { getCookie } from "../../../utils/cookieHandler";

/**
 * Inner component — only rendered once we have the real singleton id.
 * This ensures useFormLogic is never called with id=null, which caused
 * the redirect to 404 via a bad GET /api/general/undefined call.
 */
function GeneralForm({ id, apiRoute, initFields }) {
  const {
    loading,
    editFields,
    isSaving,
    setIsSaving,
    handleSubmit,
    onFormCancel,
    editId,
    handleRelatedData,
    handleSingleCreateData,
    formDataChanged,
    callPopUp,
    disableParams,
    reservationCode,
    popupData,
    resetPopUp,
  } = useFormLogic({
    apiRoute,
    id,
    initFields,
    onlyForm: undefined,
    getPromises: () => [],
    tabs: undefined,
  });

  if (loading) {
    return (
      <div className="flex h-64 justify-center items-center">
        <PropagateLoader color="#5729BC" />
      </div>
    );
  }

  return (
    <>
      <ReusableForm
        formType={"edit"}
        fields={editFields}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        onSubmit={handleSubmit}
        onCancel={onFormCancel}
        tabsData={undefined}
        handleRelatedData={handleRelatedData}
        handleSingleCreateData={handleSingleCreateData}
        updateShowFields={formDataChanged}
        id={editId ?? id}
        onlyForm={undefined}
        callPopUp={callPopUp}
        hideSubmitActions={true}
        headerSaveBotton={true}
        disableConditionsFunction={disableParams}
        reservationCode={reservationCode}
      />
      <PopUp
        autoClose={popupData.autoClose}
        isError={popupData.isError}
        title={popupData.title}
        description={popupData.description}
        formOptions={popupData.formOptions}
        takeActionText={popupData.takeActionText}
        takeActionFun={popupData.takeActionFun}
        showPopUp={popupData.showPopUp}
        hidePopUp={resetPopUp}
        formSubmit={popupData.formSubmit}
        formConcatData={popupData.formConcatData}
        propName={popupData.propName}
        disabledForm={popupData.disabledForm}
        hideCancel={popupData.hideCancel}
        hideMainButton={popupData.hideMainButton}
        showLoading={popupData.hideMainButton}
        progressValue={popupData.progressValue}
        overflow={popupData.overflow}
        expand={popupData.expand}
      >
        {popupData.children}
      </PopUp>
    </>
  );
}

/**
 * GeneralPage — Settings > General (singleton)
 *
 * Fetches the singleton record id first, then mounts GeneralForm
 * with the real id so useFormLogic never calls /api/general/undefined.
 */
function GeneralPage() {
  const [settingsId, setSettingsId] = useState(null);
  const [loadingId, setLoadingId] = useState(true);
  const [error, setError] = useState(false);

  const { apiRoute, initFields } = useGeneralLogic();

  useEffect(() => {
    const fetchId = async () => {
      try {
        const res = await axios.get(`${config.apiUrl}/api/general`, {
          headers: { Authorization: `Bearer ${getCookie("_AT")}` },
        });
        // API now returns the raw record object directly (no wrapper)
        const data = res.data;
        if (data?.id) {
          setSettingsId(String(data.id));
        } else {
          setSettingsId("new");
        }
      } catch (err) {
        console.error("[GeneralPage] Failed to fetch general settings:", err);
        setError(true);
      } finally {
        setLoadingId(false);
      }
    };
    fetchId();
  }, []);

  return (
    <div>
      <Helmet>
        <title>Admin | Glide – General Settings</title>
        <meta name="description" content="Manage general system settings" />
      </Helmet>

      {loadingId ? (
        <div className="flex h-64 justify-center items-center">
          <PropagateLoader color="#5729BC" />
        </div>
      ) : error ? (
        <p className="text-red-500 p-8">Failed to load general settings.</p>
      ) : (
        // Key forces a full remount if id ever changes
        <GeneralForm
          key={settingsId}
          id={settingsId}
          apiRoute={apiRoute}
          initFields={initFields}
        />
      )}
    </div>
  );
}

export default GeneralPage;
